// Game-over overlay component — shown when the party wipes.
// Mirrors the victory-overlay structure with a red/dark theme.

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

class GameOverOverlay extends BaseComponent {
  static properties = {
    show: { type: Boolean },
  };

  static styles = css`
    :host {
      display: block;
    }

    .game-over-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(20, 0, 0, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      animation: fadeIn 1s ease-out;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    .game-over-content {
      text-align: center;
      color: #f87171;
      animation: scaleIn 0.8s ease-out;
    }

    @keyframes scaleIn {
      from {
        transform: scale(0.8);
        opacity: 0;
      }
      to {
        transform: scale(1);
        opacity: 1;
      }
    }

    .game-over-icon {
      margin-bottom: 1rem;
    }

    .game-over-title {
      font-size: 2.5rem;
      font-weight: bold;
      margin: 0 0 0.5rem 0;
      color: #fca5a5;
      text-shadow: 0 0 20px rgba(239, 68, 68, 0.5);
    }

    .game-over-message {
      font-size: 1.125rem;
      color: #a1a1aa;
      margin: 0 0 2rem 0;
    }

    .button-row {
      display: flex;
      gap: 1rem;
      justify-content: center;
    }

    .quit-btn {
      background: #333;
      color: #e0e0e0;
      border: 1px solid #555;
      padding: 0.75rem 2rem;
      font-size: 1.1rem;
      font-weight: 600;
      border-radius: 8px;
      cursor: pointer;
      transition:
        transform 0.2s,
        background 0.2s;
    }

    .quit-btn:hover {
      background: #444;
      transform: scale(1.03);
    }

    .retry-btn {
      background: linear-gradient(135deg, #dc2626, #991b1b);
      color: #fff;
      border: none;
      padding: 0.75rem 2rem;
      font-size: 1.1rem;
      font-weight: bold;
      border-radius: 8px;
      cursor: pointer;
      transition:
        transform 0.2s,
        box-shadow 0.2s;
    }

    .retry-btn:hover {
      transform: scale(1.05);
      box-shadow: 0 0 20px rgba(220, 38, 38, 0.5);
    }

    .retry-btn:active {
      transform: scale(0.98);
    }

    .icon {
      width: 64px;
      height: 64px;
    }
  `;

  constructor() {
    super();
    this.show = false;
  }

  _handleQuit() {
    this.dispatchEvent(new CustomEvent('quit-game', { bubbles: true }));
  }

  _handleRetry() {
    this.dispatchEvent(new CustomEvent('retry-game', { bubbles: true }));
  }

  _renderSkullIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="9" cy="12" r="1" />
        <circle cx="15" cy="12" r="1" />
        <path d="M8 20v2h8v-2" />
        <path d="m12.5 17-.5-1-.5 1h1z" />
        <path d="M16 20a2 2 0 0 0 1.56-3.25 8 8 0 1 0-11.12 0A2 2 0 0 0 8 20" />
      </svg>
    `;
  }

  render() {
    if (!this.show) {
      return html``;
    }

    return html`
      <div class="game-over-overlay">
        <div class="game-over-content">
          <div class="game-over-icon">${this._renderSkullIcon()}</div>
          <h1 class="game-over-title">Your Party Has Fallen</h1>
          <p class="game-over-message">All hope is lost. Your adventure ends here.</p>
          <div class="button-row">
            <button class="quit-btn" @click="${this._handleQuit}">Quit</button>
            <button class="retry-btn" @click="${this._handleRetry}">Retry</button>
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('game-over-overlay', GameOverOverlay);
